import React from "react";
import LegalLayout from "../components/LegalLayout";

const Section = ({ id, title, children }) => (
  <section id={id}>
    <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">{title}</h2>
    <div className="space-y-3 text-white/75">{children}</div>
  </section>
);

const Integritet = () => (
  <div data-testid="page-integritet">
    <LegalLayout
      eyebrow="Integritet"
      title="Integritetspolicy"
      updated="2026-07-05"
    >
      <p>
        Din integritet är viktig för oss. I denna policy beskriver Redoc AB hur
        vi behandlar personuppgifter som samlas in via landningssidan{" "}
        <strong>orkesteradmin.se</strong>. Behandlingen sker i enlighet med
        EU:s dataskyddsförordning (GDPR).
      </p>

      <Section id="ansvarig" title="1. Personuppgiftsansvarig">
        <p>
          <strong>Redoc AB</strong>
          <br />
          E-post:{" "}
          <span className="text-white">kontakt.orkesteradmin@gmail.com</span>
        </p>
      </Section>

      <Section id="uppgifter" title="2. Vilka uppgifter samlar vi in?">
        <p>
          Vi samlar endast in uppgifter som du själv lämnar via
          kontaktformuläret. Följande fält kan behandlas:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Namn</li>
          <li>E-postadress</li>
          <li>Orkester/organisation (frivilligt)</li>
          <li>Telefonnummer (frivilligt)</li>
          <li>Val av kontakttyp (Boka demo / Frågor / Support)</li>
          <li>Ämne och meddelandeinnehåll</li>
          <li>Teknisk metadata (tidsstämpel, unikt ärende-ID)</li>
        </ul>
        <p>
          Utöver detta läses tekniska uppgifter automatiskt (t.ex. IP-adress
          och webbläsare) vid besök på sidan för säkerhet och statistik. Se{" "}
          <a href="/cookies" className="text-[#d4af37] hover:underline">
            Cookiepolicy
          </a>{" "}
          för mer information.
        </p>
      </Section>

      <Section id="andamal" title="3. Ändamål och rättslig grund">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Besvara din förfrågan.</strong> Rättslig grund: berättigat
            intresse (art. 6.1 f GDPR) samt fullgörande av åtgärder inför avtal
            (art. 6.1 b) när du begär demo eller offert.
          </li>
          <li>
            <strong>Administrera beställning</strong> via Stripe. När du klickar
            på "Beställ" hanteras betalningen av Stripe enligt deras egna
            villkor och integritetspolicy.
          </li>
          <li>
            <strong>Säkerhet och drift av webbplatsen.</strong> Rättslig grund:
            berättigat intresse att skydda sidan mot missbruk.
          </li>
        </ul>
      </Section>

      <Section id="mottagare" title="4. Vem får ta del av uppgifterna?">
        <p>
          Uppgifter delas endast med aktörer som behöver dem för att vi ska
          kunna svara dig eller driva sidan:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Molnbaserad e-postleverantör</strong> (Resend och/eller
            Google/Gmail) för att skicka och ta emot mejl.
          </li>
          <li>
            <strong>Molnbaserad databasleverantör</strong> för säker lagring av
            din förfrågan.
          </li>
          <li>
            <strong>Stripe</strong> för betalningshantering vid beställning.
          </li>
          <li>
            <strong>PostHog</strong> för anonym användningsstatistik (se{" "}
            <a href="/cookies" className="text-[#d4af37] hover:underline">
              Cookiepolicy
            </a>
            ).
          </li>
        </ul>
        <p>
          Vi säljer aldrig dina uppgifter. Överföring utanför EU/EES sker
          endast med lämpliga skyddsåtgärder enligt GDPR.
        </p>
      </Section>

      <Section id="lagring" title="5. Hur länge sparas uppgifterna?">
        <p>
          Kontaktförfrågningar sparas så länge det är nödvändigt för att
          besvara dig och för administrativa ändamål, dock högst{" "}
          <strong>24 månader</strong> efter senaste kontakt. Uppgifter kopplade
          till fakturering och beställning sparas så länge svensk
          bokföringslagstiftning kräver (normalt 7 år).
        </p>
      </Section>

      <Section id="rattigheter" title="6. Dina rättigheter">
        <p>Du har enligt GDPR rätt att:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>begära ett registerutdrag,</li>
          <li>få felaktiga uppgifter rättade,</li>
          <li>få dina uppgifter raderade ("rätten att bli glömd"),</li>
          <li>begära begränsning av behandlingen,</li>
          <li>invända mot behandling som baseras på berättigat intresse,</li>
          <li>få ut dina uppgifter i ett maskinläsbart format (dataportabilitet),</li>
          <li>
            lämna in klagomål till <strong>Integritetsskyddsmyndigheten</strong>{" "}
            (
            <a
              href="https://www.imy.se/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d4af37] hover:underline"
            >
              imy.se
            </a>
            ).
          </li>
        </ul>
        <p>
          Kontakta oss på{" "}
          <span className="text-white">kontakt.orkesteradmin@gmail.com</span>{" "}
          för att utöva dina rättigheter.
        </p>
      </Section>

      <Section id="sakerhet" title="7. Säkerhet">
        <p>
          Vi vidtar tekniska och organisatoriska åtgärder för att skydda dina
          uppgifter mot obehörig åtkomst, förlust och ändring. All
          dataöverföring sker över krypterade anslutningar (HTTPS/TLS).
        </p>
      </Section>

      <Section id="andringar" title="8. Ändringar av policyn">
        <p>
          Vi kan komma att uppdatera integritetspolicyn. Den senaste versionen
          publiceras alltid här med datumet för uppdatering.
        </p>
      </Section>
    </LegalLayout>
  </div>
);

export default Integritet;
